import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';

import {
  buildHoverParentClassName,
  hoverSelector,
} from '../../../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {useDataEventTracking} from '../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppDispatch} from '../../../../../../helpers/appReduxHelpers';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {popoutConversationViewerAction} from '../../../../../../store/panels/viewer/viewerPanelActions';
import {ConversationPreviewContainer} from '../../../../../common/conversations/conversationPreviewContainer';
import {ConversationCellContainer} from '../../../../cells/conversationCellContainer';

/*
 * Props.
 */

interface ConversationHeaderAiSimilarDropdownConversationProps {
  conversationId: number;
  conversation?: ConversationModel;
  cellsContext: CellsContext;
  panelId: ViewerPanelId;
  index: number;
}

/*
 * Style.
 */

const StyledConversationViewButton = styled(ChromelessButton)`
  min-height: 84px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.alphas.black20};
`;

const StyledOverlayDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  padding: 7px 8px 8px 8px;

  border-radius: 8px;
  background-color: ${(p) => p.theme.alphas.white60};

  transition: opacity 300ms ease;
  opacity: 0;

  ${hoverSelector} {
    opacity: 1;
  }
`;

const StyledDetailsDiv = styled.div`
  display: grid;
  grid-template-areas: 'text icon';
  grid-template-columns: 1fr auto;
  grid-column-gap: 4px;
  align-items: center;
`;

const StyledTextDiv = styled.div`
  grid-area: text;
  padding-left: 4px;
  font-weight: ${fontWeights.semibold};
`;

const StyledIconDiv = styled.div`
  grid-area: icon;
  padding: 2px;
  margin-right: 2px;
  border-radius: 50%;
  background: ${(p) => p.theme.palette.blue.shade40};
`;

/*
 * Component.
 */

export const ConversationHeaderAiSimilarDropdownConversation: FC<
  ConversationHeaderAiSimilarDropdownConversationProps
> = ({conversationId, conversation, cellsContext, panelId, index}) => {
  const buttonRef = useRef(null);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);
  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();

  const onConversationEnter = () => {
    setShouldShowPreview(true);
    performance.mark(`similar_conversation_result_${conversationId}_hover_start`);
  };

  const onConversationExit = () => {
    const hoverDuration = performance.measure(
      `similar_conversation_result_${conversationId}_hover`,
      `similar_conversation_result_${conversationId}_hover_start`,
    );
    performance.clearMarks(`similar_conversation_result_${conversationId}_hover_start`);
    performance.clearMeasures(`similar_conversation_result_${conversationId}_hover`);
    trackDataEvent(DataEventActionsEnum.HOVER, ViewerDataEventObject.SIMILAR_CONVERSATION_RESULT, {
      selected_conversation_id: conversationId,
      position: index + 1,
      hover_time: Math.round(hoverDuration.duration),
    });
    setShouldShowPreview(false);
  };

  if (!conversation) {
    return null;
  }
  return (
    <StyledConversationViewButton
      ref={buttonRef}
      key={conversationId}
      className={buildHoverParentClassName()}
      onMouseEnter={onConversationEnter}
      onMouseLeave={onConversationExit}
      onClick={() => {
        dispatch(popoutConversationViewerAction(conversationId));
        trackDataEvent(DataEventActionsEnum.CLICK, ViewerDataEventObject.SIMILAR_CONVERSATION_RESULT, {
          selected_conversation_id: conversationId,
          position: index + 1,
        });
      }}
    >
      <ConversationCellContainer
        panelId={panelId}
        conversation={conversation}
        cellsContext={cellsContext}
        isExtraConversation={false}
        parameters={undefined}
        isTransparentPreview
      />
      {renderOverlay()}
      {shouldShowPreview && (
        <ConversationPreviewContainer
          panelId={panelId}
          conversation={conversation}
          anchor={buttonRef}
          viewerConversationId={undefined}
          shouldRenderSummaryIfAvailable
        />
      )}
    </StyledConversationViewButton>
  );
};

/*
 * Helpers.
 */

function renderOverlay() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledOverlayDiv>
          <StyledDetailsDiv>
            <StyledTextDiv>
              <Trans id="conversation-header-ai-similar-converstaions-view-in-new-window">
                View in new window
              </Trans>
            </StyledTextDiv>
            <StyledIconDiv>
              <Icon name="externalLinkTiny" color={theme.greys.white} />
            </StyledIconDiv>
          </StyledDetailsDiv>
        </StyledOverlayDiv>
      )}
    </UserThemeConsumer>
  );
}
